import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Loader2, Send, Check } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import ChecklistGate from "@/components/ChecklistGate";
import { useTitle } from "@/hooks/use-title";
import {
  DIAGNOSTIC_QUESTIONS,
  DIAGNOSTIC_DIMS,
  DIAGNOSTIC_OPTIONS,
  DIAGNOSTIC_RESULT_RANGES,
  DIAGNOSTIC_OVERALL_RANGES,
} from "@/data/content";

const DIM_ORDER = ["process", "people", "technology", "governance", "automation"];
const API = process.env.REACT_APP_BACKEND_URL || "";

function getRangeForScore(dim, score) {
  const ranges = DIAGNOSTIC_RESULT_RANGES[dim] || [];
  for (const r of ranges) {
    if (score >= r.min && score <= r.max) return r;
  }
  return ranges[ranges.length - 1] || { label: "", summary: "" };
}

function getOverallLabel(total) {
  for (const r of DIAGNOSTIC_OVERALL_RANGES) {
    if (total >= r.min && total <= r.max) return r.label;
  }
  return DIAGNOSTIC_OVERALL_RANGES[DIAGNOSTIC_OVERALL_RANGES.length - 1]?.label || "";
}

function computeLocalScores(answers) {
  const dimScores = {};
  DIM_ORDER.forEach((d) => (dimScores[d] = 0));
  DIAGNOSTIC_QUESTIONS.forEach((q, i) => {
    if (answers[i]) dimScores[q.dim] += answers[i];
  });
  const dimensions = {};
  DIM_ORDER.forEach((dim) => {
    const score = dimScores[dim];
    const range = getRangeForScore(dim, score);
    dimensions[dim] = { score, max: 15, label: range.label, summary: range.summary };
  });
  const sorted = Object.entries(dimensions).sort((a, b) => b[1].score - a[1].score);
  const overall = Object.values(dimScores).reduce((a, b) => a + b, 0);
  return {
    dimensions,
    top_dimension: sorted[0][0],
    overall_score: overall,
    overall_max: 75,
    overall_label: getOverallLabel(overall),
  };
}

export default function Diagnostic() {
  useTitle("Diagnostic — Operating Reality");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const source = searchParams.get("source") || "";
  const campaign = searchParams.get("campaign") || "";

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diagnosticId, setDiagnosticId] = useState("");
  const submittedRef = useRef(false);

  // post-result UI state
  const [uiState, setUiState] = useState("summary"); // summary | get_result | result_sent | share
  const [token, setToken] = useState("");
  const [resultUrl, setResultUrl] = useState("");

  const done = step >= DIAGNOSTIC_QUESTIONS.length;

  useEffect(() => {
    if (!done || submittedRef.current || answers.length < DIAGNOSTIC_QUESTIONS.length) return;
    submittedRef.current = true;
    setLoading(true);
    const payload = { answers, source, campaign };
    fetch(`${API}/api/diagnostic`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.result) {
          setResult(data.result);
          setDiagnosticId(data.diagnostic_id || "");
        } else {
          setResult(computeLocalScores(answers));
        }
      })
      .catch(() => setResult(computeLocalScores(answers)))
      .finally(() => setLoading(false));
  }, [done, answers, source, campaign]);

  const answer = (value) => {
    const next = [...answers, value];
    setAnswers(next);
    setTimeout(() => setStep((s) => s + 1), 280);
  };

  const back = () => {
    setAnswers((a) => a.slice(0, -1));
    setStep((s) => Math.max(0, s - 1));
  };

  const restart = () => {
    setAnswers([]);
    setStep(0);
    setResult(null);
    setDiagnosticId("");
    setLoading(false);
    setUiState("summary");
    setToken("");
    setResultUrl("");
    submittedRef.current = false;
  };

  const scores = result
    ? DIM_ORDER.map((dim) => ({
        dim,
        total: result.dimensions[dim]?.score || 0,
        max: 15,
        label: result.dimensions[dim]?.label || "",
        summary: result.dimensions[dim]?.summary || "",
      }))
    : DIM_ORDER.map((dim) => ({ dim, total: 0, max: 15, label: "", summary: "" }));

  const top = result ? result.top_dimension : scores[0]?.dim;

  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="diagnostic-page">
      <section className="relative min-h-[calc(100vh-72px)] overflow-hidden">
        <div className="bg-grid absolute inset-0 opacity-25" />
        <div className="wrap relative flex min-h-[calc(100vh-72px)] flex-col justify-center py-20">
          {!done ? (
            <div className="mx-auto w-full max-w-3xl">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                <span>Operating Reality Diagnostic</span>
                <span data-testid="diagnostic-step-counter">
                  {String(Math.min(step + 1, 15)).padStart(2, "0")} / 15
                </span>
              </div>
              <div className="mt-4 h-px w-full bg-line/60">
                <motion.div
                  className="h-px bg-glow"
                  animate={{ width: `${(step / DIAGNOSTIC_QUESTIONS.length) * 100}%` }}
                  transition={{ duration: 0.5, ease: EASE }}
                  data-testid="diagnostic-progress-bar"
                />
              </div>

              <div className="mt-16 min-h-[280px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.45, ease: EASE }}
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-glow/70">
                      {DIAGNOSTIC_DIMS[DIAGNOSTIC_QUESTIONS[step].dim].label}
                    </p>
                    <h1
                      className="mt-5 text-balance text-2xl font-medium leading-snug tracking-tight text-slate-100 sm:text-3xl lg:text-4xl"
                      data-testid="diagnostic-question-text"
                    >
                      {DIAGNOSTIC_QUESTIONS[step].text}
                    </h1>
                    <div className="mt-12 grid gap-px border border-line/50 bg-line/50 sm:grid-cols-5">
                      {DIAGNOSTIC_OPTIONS.map((label, i) => (
                        <button
                          key={label}
                          onClick={() => answer(i + 1)}
                          className={`group bg-ink px-4 py-5 text-left transition-colors duration-200 hover:bg-charcoal ${
                            answers[step] === i + 1 ? "!bg-glow/10" : ""
                          }`}
                          data-testid={`diagnostic-option-${i + 1}`}
                        >
                          <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600 group-hover:text-glow">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="mt-2 block text-xs font-medium uppercase tracking-[0.12em] text-slate-300 group-hover:text-white">
                            {label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <button
                  onClick={back}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors hover:text-glow disabled:opacity-30"
                  data-testid="diagnostic-back-button"
                >
                  <ArrowLeft size={13} /> Back
                </button>
                <p className="text-xs text-slate-600">Nothing leaves your browser until the end.</p>
              </div>
            </div>
          ) : loading ? (
            <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center py-32">
              <Loader2 className="h-8 w-8 animate-spin text-glow" />
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                Processing your answers
              </p>
            </div>
          ) : result ? (
            <div className="mx-auto w-full max-w-3xl" data-testid="diagnostic-results">

              {/* Score summary — always visible */}
              <Reveal>
                <p className="kicker">The result</p>
                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-mono text-6xl font-semibold tracking-tight text-slate-100 lg:text-7xl" data-testid="diagnostic-overall-score">
                    {result.overall_score}
                  </span>
                  <span className="font-mono text-2xl text-slate-500">/ 75</span>
                </div>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-glow/60">
                  {result.overall_label}
                </p>
                <h1 className="mt-5 text-balance text-3xl font-medium leading-[1.15] tracking-tight text-slate-100 lg:text-4xl">
                  Start with {DIAGNOSTIC_DIMS[top].label.toLowerCase()}.
                </h1>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-glow/60">
                  {result.dimensions[top]?.label}
                </p>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
                  {result.dimensions[top]?.summary || DIAGNOSTIC_DIMS[top].narrative}
                </p>
              </Reveal>

              {/* Two primary CTAs */}
              <Reveal delay={0.15}>
                <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                  <button
                    className="btn-primary"
                    onClick={() => setUiState(uiState === "get_result" ? "summary" : "get_result")}
                    data-testid="diagnostic-get-result-btn"
                  >
                    Get my full result <ArrowRight size={14} />
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => {
                      if (!token) setUiState(uiState === "share" ? "summary" : "get_result");
                      else setUiState(uiState === "share" ? "summary" : "share");
                    }}
                    data-testid="diagnostic-share-btn"
                  >
                    <Send size={13} /> Share my result
                  </button>
                </div>
              </Reveal>

              {/* GET MY FULL RESULT form */}
              {uiState === "get_result" && (
                <Reveal delay={0.1}>
                  <EmailResultForm
                    diagnosticId={diagnosticId}
                    source={source}
                    campaign={campaign}
                    onSuccess={({ token: t, result_url: u }) => {
                      setToken(t);
                      setResultUrl(u);
                      setUiState("result_sent");
                    }}
                  />
                </Reveal>
              )}

              {/* Confirmation */}
              {uiState === "result_sent" && (
                <Reveal delay={0.1}>
                  <div className="mt-10 border border-line/40 bg-ink/50 p-8" data-testid="diagnostic-result-sent">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-glow">Your result is on its way</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">
                      Check your inbox for your personalised Operating Reality result.
                    </p>
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                      {resultUrl && (
                        <a href={resultUrl} className="btn-primary" data-testid="diagnostic-view-result-now">
                          View my result now <ArrowRight size={14} />
                        </a>
                      )}
                      <button className="btn-ghost" onClick={() => setUiState("share")} data-testid="diagnostic-share-after-send">
                        <Send size={13} /> Share my result
                      </button>
                    </div>
                  </div>
                </Reveal>
              )}

              {/* SHARE form */}
              {uiState === "share" && (
                <Reveal delay={0.1}>
                  <ShareResultForm token={token} />
                </Reveal>
              )}

              {/* Dimension bars */}
              <div className="mt-14 space-y-6">
                {scores.map((s, i) => (
                  <Reveal key={s.dim} delay={i * 0.08}>
                    <div data-testid={`diagnostic-result-${s.dim}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="font-mono text-xs uppercase tracking-[0.22em] text-slate-300">
                          {DIAGNOSTIC_DIMS[s.dim].label}
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.2em] text-slate-600">
                          {s.label && <span className="mr-3 text-slate-500">{s.label}</span>}
                          {s.total} / {s.max}
                        </span>
                      </div>
                      <div className="mt-2.5 h-px w-full bg-line/60">
                        <motion.div
                          className={`h-px ${s.dim === top ? "bg-glow" : "bg-slate-500"}`}
                          initial={{ width: 0 }}
                          whileInView={{ width: `${(s.total / s.max) * 100}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, delay: 0.3 + i * 0.12, ease: EASE }}
                        />
                      </div>
                      {s.summary && (s.total >= 9 || s.dim === top) && (
                        <p className="mt-3 max-w-2xl text-xs leading-relaxed text-slate-500">{s.summary}</p>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>

              {/* Consultation CTA */}
              <Reveal delay={0.4}>
                <div className="mt-14 flex flex-col gap-3 sm:flex-row">
                  <a href="/contact" className="btn-primary" data-testid="diagnostic-contact-cta">
                    Talk to Operating Reality <ArrowRight size={14} />
                  </a>
                  <button onClick={restart} className="btn-ghost" data-testid="diagnostic-restart-button">
                    <RotateCcw size={13} /> Run it again
                  </button>
                </div>
                <p className="mt-8 max-w-xl text-xs leading-relaxed text-slate-600">
                  This is an orienting instrument, not a verdict. A real diagnosis starts with how
                  the work actually happens — that's the conversation.
                </p>
              </Reveal>

              <Reveal delay={0.6}>
                <div className="mt-12">
                  <ChecklistGate />
                </div>
              </Reveal>
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

/* ── Email result form ───────────────────────────────────────────────── */

function EmailResultForm({ diagnosticId, source, campaign, onSuccess }) {
  const [form, setForm] = useState({ email: "", name: "", organisation: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | error

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    if (form.website) return; // honeypot
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/diagnostic/email-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          diagnostic_id: diagnosticId,
          email: form.email.trim(),
          name: form.name.trim(),
          organisation: form.organisation.trim(),
          source,
          campaign,
          website: form.website,
        }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        onSuccess({ token: data.token, result_url: data.result_url });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 border border-line/40 bg-ink/50 p-8" data-testid="diagnostic-email-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">Get my full result</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-400">
        We'll email your personalised result and create a shareable link.
      </p>

      {/* Honeypot */}
      <input type="text" name="website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} className="absolute -left-[9999px] h-0 w-0 opacity-0" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="er-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Work email</label>
          <input id="er-email" type="email" required maxLength={254} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="you@company.com" data-testid="diagnostic-email-input" />
        </div>
        <div>
          <label htmlFor="er-name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Name <span className="text-slate-700">(optional)</span></label>
          <input id="er-name" type="text" maxLength={120} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="Your name" data-testid="diagnostic-name-input" />
        </div>
      </div>
      <div className="mt-4">
        <label htmlFor="er-org" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Organisation <span className="text-slate-700">(optional)</span></label>
        <input id="er-org" type="text" maxLength={160} value={form.organisation} onChange={e => setForm(f => ({ ...f, organisation: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="Company or team" data-testid="diagnostic-org-input" />
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button type="submit" disabled={status === "sending"} className="btn-primary disabled:opacity-50" data-testid="diagnostic-email-submit">
          {status === "sending" ? <><Loader2 size={13} className="animate-spin" /> Sending</> : <>Email my result <ArrowRight size={14} /></>}
        </button>
        {status === "error" && <p className="text-xs text-red-400">Something went wrong. Try again.</p>}
      </div>
    </form>
  );
}

/* ── Share result form ───────────────────────────────────────────────── */

function ShareResultForm({ token }) {
  const [form, setForm] = useState({ recipient_email: "", recipient_name: "", message: "I completed this Operating Reality diagnostic and thought the result might be useful for us to discuss.", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipient_email.trim()) return;
    if (form.website) return; // honeypot
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/diagnostic/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, ...form }),
      });
      setStatus(res.ok ? "sent" : "error");
    } catch { setStatus("error"); }
  };

  if (status === "sent") return (
    <div className="mt-10 border border-line/40 bg-ink/50 p-8" data-testid="diagnostic-share-success">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-glow"><Check size={12} className="inline mr-2" />Result sent</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-400">Your result has been shared.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="mt-10 border border-line/40 bg-ink/50 p-8" data-testid="diagnostic-share-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">Share my result</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-400">Send your result to a colleague or contact.</p>

      {/* Honeypot */}
      <input type="text" name="website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} className="absolute -left-[9999px] h-0 w-0 opacity-0" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="sh-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Recipient email</label>
          <input id="sh-email" type="email" required maxLength={254} value={form.recipient_email} onChange={e => setForm(f => ({ ...f, recipient_email: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="colleague@company.com" data-testid="share-recipient-email" />
        </div>
        <div>
          <label htmlFor="sh-name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Recipient name <span className="text-slate-700">(optional)</span></label>
          <input id="sh-name" type="text" maxLength={120} value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" placeholder="Their name" />
        </div>
      </div>
      <div className="mt-4">
        <label htmlFor="sh-msg" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Message <span className="text-slate-700">(optional)</span></label>
        <textarea id="sh-msg" rows={3} maxLength={500} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="w-full border border-line/50 bg-transparent px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-700 focus:border-glow/50 focus:outline-none" />
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button type="submit" disabled={status === "sending"} className="btn-primary disabled:opacity-50" data-testid="share-submit">
          {status === "sending" ? <><Loader2 size={13} className="animate-spin" /> Sending</> : <>Send result <ArrowRight size={14} /></>}
        </button>
        {status === "error" && <p className="text-xs text-red-400">Something went wrong. Try again.</p>}
      </div>
    </form>
  );
}
